import { useEffect, useState } from "react";
import ReactExport from "react-export-excel";
import {
  Grid,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Button,
  Switch,
  Typography,
  Divider,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import AutoCompleteField from "components/_refactor/fields/autocomplete";
import TableTitulos from "../components/table-titulos-summarized";
import TableTitulosFull from "../components/table-titulos-full";
import DialogExport from "views/Financeiro/acompanhamento-titulos/components/dialog-export";
import Api from "lib/api/httpRequestHelper";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import LoadingData from "components/Loading/LoadingData";
import TableChartIcon from "@mui/icons-material/TableChart";

const ExcelFile = ReactExport.ExcelFile;
const ExcelSheet = ReactExport.ExcelFile.ExcelSheet;
const ExcelColumn = ReactExport.ExcelFile.ExcelColumn;

const statusOptions = [
  { id: "todos", Nome: "Todos" },
  { id: "em_aberto", Nome: "Em Aberto" },
  { id: "pago", Nome: "Pago" },
];

const statusMap = {
  todos: "TODOS",
  em_aberto: "AB",
  pago: "LQ",
};

export const LONGTERM_STATUS = new Map([
  ["AB", "Aberto"],
  ["CA", "Cancelado"],
  ["LQ", "Liquidado"],
  ["LS", "Liquidado Substituído"],
]);

const ExportComponent = ({ data, dataInicial, dataFinal, loading }) => {
  return (
    <ExcelFile
      element={
        <Tooltip title="Exportar Excel">
          <IconButton size="small" disabled={loading} color="gray">
            <TableChartIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      }
      filename={`titulos-financeiros-${dataInicial || ""}-${dataFinal || ""}`}
    >
      <ExcelSheet data={data} name="Títulos">
        <ExcelColumn label="Nr Título" value="NumeroTitulo" />
        <ExcelColumn label="Situação" value={(row) => LONGTERM_STATUS.get(row.Situacao) || row.Situacao} />
        <ExcelColumn label="Emissão" value="DataEmissao" />
        <ExcelColumn label="Vencimento" value="DataVencimentoProrrogado" />
        <ExcelColumn label="Valor" value="Valor" />
        <ExcelColumn label="Valor em Aberto" value="ValorEmAberto" />
        <ExcelColumn label="Marca" value="Marca" />
      </ExcelSheet>
    </ExcelFile>
  );
}

export default function TitulosFinanceiros({
  structureFilters,
  cnpj = "",
  dataInicial,
  dataFinal,
}) {
  const [tipoData, setTipoData] = useState("vencimento");
  const [status, setStatus] = useState(statusOptions[0]);
  const [data, setData] = useState([]);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isFullView, setIsFullView] = useState(false);
  const [openExportBoletos, setOpenExportBoletos] = useState(false);
  const [selectedTitles, setSelectedTitles] = useState([]);

  const snackbar = useSnackbar();
  const hasStructure =
    structureFilters?.idGrupoEconomico || structureFilters?.idLoja?.length;

  const handleFilter = async () => {
    if (!hasStructure && !cnpj) {
      snackbar.showMessage(
        "Selecione Grupo econômico e/ou Lojas e/ou Marcas e/ou Linha OU informe CNPJ e/ou Marca e/ou Linha",
        { severity: "error" }
      );
      return;
    }

    setLoading(true);

    try {
      const params = new URLSearchParams({
        idMarca: structureFilters?.idMarca || "TODAS",
        idGrupoEconomico: structureFilters?.idGrupoEconomico || "",
        idLoja: structureFilters?.idLoja?.length ? structureFilters.idLoja : "",
        cnpj: structureFilters?.cnpj?.replace(/\D/g, "") || "",
        dtIni: dataInicial,
        dtFim: dataFinal,
        tipoData: tipoData,
        status: statusMap[status?.id] || "TODOS",
      });

      [...params.entries()]
        .filter(
          ([, value]) => value === undefined || value === null || value === ""
        )
        .forEach(([key]) => params.delete(key));

      const response = await Api.get(
        "visao360-titulos",
        "reportApp",
        `?${params.toString()}`,
        true
      );

      const result = response?.Result;

      if (result) {
        const titulos = result.Titulos || [];
        const total = result.TotalRegistros || 0;
        setData(titulos);
        setTotalRegistros(total);
      } else {
        setData([]);
        setTotalRegistros(0);
      }
    } catch (err) {
      snackbar.showMessage(
        err.message || "Erro ao carregar dados de títulos financeiros",
        {
          severity: "error",
        }
      );
      setData([]);
      setTotalRegistros(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleFilter();
  }, []);

  const handleExportBoletos = (selectedRows) => {
    setSelectedTitles(
      selectedRows.map((row) => ({
        ...row,
        UrlDownloadBoleto: row.UrlBoleto ?? row.UrlDownloadBoleto,
      })),
    );
    setOpenExportBoletos(true);
  };

  return (
    <>
      <Grid container spacing={2} style={{ margin: "1rem 0 0" }}>
        <Grid item xs={12} lg={4}>
          <FormGroup>
            <FormLabel>Tipo de Data</FormLabel>
            <RadioGroup
              row
              value={tipoData}
              onChange={(e) => setTipoData(e.target.value)}
            >
              <FormControlLabel
                value="emissao"
                control={<Radio color="primary" />}
                label="Data de Emissão"
              />
              <FormControlLabel
                value="vencimento"
                control={<Radio color="primary" />}
                label="Data de Vencimento"
              />
            </RadioGroup>
          </FormGroup>
        </Grid>
        <Grid item xs={12} lg={3}>
          <AutoCompleteField
            label="Status"
            options={statusOptions}
            value={status}
            onChange={setStatus}
            optionLabelKey="Nome"
            optionIdKey="id"
          />
        </Grid>
        <Grid item xs={12} lg={3} justify="center">
          <FormControlLabel
            control={
              <Switch
                checked={isFullView}
                onChange={(e) => setIsFullView(e.target.checked)}
                disabled={loading}
                color="primary"
              />
            }
            label={
              <Typography variant="body2" style={{ marginLeft: 8 }}>
                Visualização Detalhada
              </Typography>
            }
            style={{ marginTop: "1rem" }}
          />
        </Grid>
        <Grid item xs={12} lg={2} justify="flex-end" style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {data?.length > 0 && (
            <>
              
            </>
          )}
          <Button
            variant="contained"
            onClick={() => handleFilter({})}
            disabled={loading}
          >
            Filtrar
          </Button>
        </Grid>
      </Grid>

      <Grid container>
        <Grid item xs={12} style={{ margin: "1rem 0" }}>
          <Divider />
        </Grid>
      </Grid>

      {loading && (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <LoadingData
              searching
              label="Buscando dados de títulos financeiros..."
            />
          </Grid>
        </Grid>
      )}

      {!loading && (
        <>
          {isFullView ? (
            <TableTitulosFull
              data={data}
              totalRegistros={totalRegistros}
              onExportBoletos={handleExportBoletos}
              extraActions={<ExportComponent data={data} dataInicial={dataInicial} dataFinal={dataFinal} loading={loading} />}
            />
        ) : (
            <TableTitulos
              data={data}
              totalRegistros={totalRegistros}
              extraActions={<ExportComponent data={data} dataInicial={dataInicial} dataFinal={dataFinal} loading={loading} />}
            />
          )}
        </>
      )}
      {openExportBoletos && (
        <DialogExport
          onClose={() => setOpenExportBoletos(false)}
          titles={selectedTitles}
        />
      )}
    </>
  );
}
